import {
  Alert,
  Box,
  Container,
  Heading,
  Link,
  Stack,
  Text,
} from "@chakra-ui/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation } from "@tanstack/react-query"
import { Link as RouterLink, useRouter } from "@tanstack/react-router"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { FiCheck, FiMail, FiUser, FiX } from "react-icons/fi"
import { z } from "zod"

import { Button } from "@/components/atoms/Button"
import { Field } from "@/components/atoms/Field"
import { Input } from "@/components/atoms/Input"
import { InputGroup } from "@/components/atoms/InputGroup"
import { PasswordInput } from "@/components/atoms/PasswordInput"
import useCustomToast from "@/hooks/useCustomToast"
import { api } from "@/services/api"

const registerSchema = z
  .object({
    email: z.string().email("Please enter a valid email address"),
    full_name: z
      .string()
      .min(1, "Full name is required")
      .max(100, "Full name must be less than 100 characters"),
    password: z
      .string()
      .min(12, "Password must be at least 12 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number")
      .regex(
        /[^A-Za-z0-9]/,
        "Password must contain at least one special character",
      ),
    confirm_password: z.string(),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: "Passwords don't match",
    path: ["confirm_password"],
  })

type RegisterFormData = z.infer<typeof registerSchema>

export interface RegisterFormProps {
  onSuccess?: (user: unknown) => void
  redirectTo?: string
}

const getPasswordStrength = (password: string) => {
  const checks = [
    { label: "At least 12 characters", passed: password.length >= 12 },
    { label: "Contains uppercase letter", passed: /[A-Z]/.test(password) },
    { label: "Contains lowercase letter", passed: /[a-z]/.test(password) },
    { label: "Contains number", passed: /[0-9]/.test(password) },
    {
      label: "Contains special character",
      passed: /[^A-Za-z0-9]/.test(password),
    },
  ]

  const score = checks.filter((check) => check.passed).length
  return { score: (score / 5) * 100, checks }
}

export const RegisterForm = ({
  onSuccess,
  redirectTo = "/login",
}: RegisterFormProps) => {
  const [showPasswordStrength, setShowPasswordStrength] = useState(false)
  const router = useRouter()
  const { showSuccessToast, showErrorToast } = useCustomToast()

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  })

  const password = watch("password", "")
  const passwordStrength = getPasswordStrength(password)

  const registerMutation = useMutation({
    mutationFn: async (data: RegisterFormData) => {
      try {
        const response = await api.auth.registerApiV1AuthRegisterPost({
          requestBody: {
            email: data.email,
            password: data.password,
            full_name: data.full_name,
          },
        })
        // The generated API client returns the response directly, not wrapped
        return response
      } catch (error: unknown) {
        const apiError = error as {
          message?: string
          status?: number
          statusText?: string
          body?: unknown
        }
        console.error("Registration error details:", {
          message: apiError?.message,
          status: apiError?.status,
          statusText: apiError?.statusText,
          body: apiError?.body,
          fullError: error,
        })
        // Check if it's a validation error
        if (apiError?.status === 422) {
          console.error("Validation error body:", apiError?.body)
        }
        throw error
      }
    },
    onSuccess: (data) => {
      showSuccessToast(
        "Registration successful! Please check your email to verify your account.",
      )
      onSuccess?.(data)
      router.navigate({ to: redirectTo })
    },
    onError: (error: Error) => {
      showErrorToast(
        `Registration failed: ${error?.message || "Unknown error"}`,
      )
    },
  })

  const onSubmit = (data: RegisterFormData) => {
    registerMutation.mutate(data)
  }

  // Debug: Log when component renders

  return (
    <Container maxW="md" py={8}>
      <Stack gap={6}>
        {/* Header */}
        <Stack align="center" gap={2}>
          <Heading size="lg">Create Account</Heading>
          <Text color="gray.600" textAlign="center">
            Join us today and get started with your new account
          </Text>
        </Stack>

        {/* Registration Form */}
        <Box
          as="form"
          noValidate
          onSubmit={(e) => {
            handleSubmit(onSubmit)(e)
          }}
          p={6}
          border="1px"
          borderColor="gray.200"
          borderRadius="lg"
          bg="white"
          shadow="sm"
        >
          <Stack gap={4}>
            {/* Full Name */}
            <Field
              label="Full Name"
              required
              invalid={!!errors.full_name}
              errorText={errors.full_name?.message}
            >
              <InputGroup startElement={<FiUser />}>
                <Input
                  {...register("full_name")}
                  placeholder="Enter your full name"
                />
              </InputGroup>
            </Field>

            {/* Email */}
            <Field
              label="Email Address"
              required
              invalid={!!errors.email}
              errorText={errors.email?.message}
            >
              <InputGroup startElement={<FiMail />}>
                <Input
                  {...register("email")}
                  type="email"
                  placeholder="Enter your email address"
                />
              </InputGroup>
            </Field>

            {/* Password */}
            <Field
              label="Password"
              required
              invalid={!!errors.password}
              errorText={errors.password?.message}
            >
              <PasswordInput
                {...register("password")}
                type="password"
                errors={errors}
                placeholder="Create a strong password"
                onFocus={() => setShowPasswordStrength(true)}
              />
            </Field>

            {/* Password Strength Indicator */}
            {showPasswordStrength && password && (
              <Stack gap={2} p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" fontWeight="medium">
                  Password Strength
                </Text>
                <Box
                  bg="gray.200"
                  h={2}
                  borderRadius="sm"
                  position="relative"
                  overflow="hidden"
                >
                  <Box
                    bg={
                      passwordStrength.score >= 80
                        ? "green.400"
                        : passwordStrength.score >= 60
                          ? "yellow.400"
                          : "red.400"
                    }
                    h="full"
                    w={`${passwordStrength.score}%`}
                    transition="width 0.2s"
                  />
                </Box>
                <Stack gap={1}>
                  {passwordStrength.checks.map((check, index) => (
                    <Box key={index} display="flex" alignItems="center" gap={2}>
                      {check.passed ? (
                        <FiCheck size={12} color="green" />
                      ) : (
                        <FiX size={12} color="red" />
                      )}
                      <Text
                        fontSize="xs"
                        color={check.passed ? "green.600" : "gray.500"}
                      >
                        {check.label}
                      </Text>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            )}

            {/* Confirm Password */}
            <Field
              label="Confirm Password"
              required
              invalid={!!errors.confirm_password}
              errorText={errors.confirm_password?.message}
            >
              <PasswordInput
                {...register("confirm_password")}
                type="password"
                errors={errors}
                placeholder="Confirm your password"
              />
            </Field>

            {/* Submit Button */}
            <Button
              type="submit"
              colorPalette="blue"
              size="lg"
              loading={isSubmitting || registerMutation.isPending}
              width="full"
              onClick={() => {}}
            >
              Create Account
            </Button>
          </Stack>
        </Box>

        {/* Login Link */}
        <Text textAlign="center" fontSize="sm" color="gray.600">
          Already have an account?{" "}
          <RouterLink to="/login">
            <Link color="blue.500" fontWeight="medium">
              Sign in here
            </Link>
          </RouterLink>
        </Text>

        {/* Email Verification Notice */}
        <Alert.Root status="info" size="sm">
          <Alert.Indicator />
          <Alert.Description>
            After registration, you'll receive an email to verify your account.
            Please check your inbox and follow the verification link.
          </Alert.Description>
        </Alert.Root>
      </Stack>
    </Container>
  )
}

export default RegisterForm
